// Top-of-window toolbar: hosts the run/reset/theme/palette buttons and
// owns the command-palette dialog. Keeps DOM wiring out of main.ts so
// the editor module can stay focused on CodeMirror state.
//
// Theme persistence lives here too — first load mirrors
// `prefers-color-scheme`, subsequent changes write `localStorage:kolilang:theme`
// and toggle `data-theme` on `<html>`. CodeMirror's editor theme is
// hot-swapped via a Compartment passed in by main.ts.

import { Compartment, type Extension } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import { kolilangDark, kolilangLight } from './editor-theme';
import { icon, type IconName } from './icons';
import * as commands from './commands';

export type Theme = 'dark' | 'light';

const THEME_KEY = 'kolilang:theme';
// Pre-rename key — migrated lazily on first read so existing users
// keep their theme choice.
const LEGACY_THEME_KEY = 'sassy:theme';

export function readTheme(): Theme {
  let stored = localStorage.getItem(THEME_KEY);
  if (stored === null) {
    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy !== null) {
      localStorage.setItem(THEME_KEY, legacy);
      localStorage.removeItem(LEGACY_THEME_KEY);
      stored = legacy;
    }
  }
  if (stored === 'dark' || stored === 'light') return stored;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

/// `data-theme` on <html> is the source of truth for our own CSS.
/// The body class mirror exists for the @kolistat/bedevere-wise
/// components (ChartVisualizer, SpreadsheetVisualizer): their
/// stylesheet keys the Paper theme family off body.theme-light /
/// body.theme-dark, and they watch body class mutations to re-theme
/// live. Both attributes must always move together — index.html's
/// pre-paint script sets the same pair before first paint.
function applyThemeAttributes(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document.body.classList.toggle('theme-dark', theme === 'dark');
  document.body.classList.toggle('theme-light', theme === 'light');
}

function writeTheme(theme: Theme): void {
  localStorage.setItem(THEME_KEY, theme);
  applyThemeAttributes(theme);
}

export function applyInitialTheme(): Theme {
  const t = readTheme();
  applyThemeAttributes(t);
  return t;
}

/// Pick the editor theme bundle that matches the page theme. The
/// bundles are app-palette-coloured (defined in `editor-theme.ts`)
/// so the editor chrome and syntax colors share the same
/// vocabulary as the rest of the UI — accent, warn, ok, fg/bg
/// CSS variable analogues.
export function editorThemeFor(theme: Theme): Extension {
  return theme === 'dark' ? kolilangDark : kolilangLight;
}

/// Wire a CodeMirror Compartment so the editor theme tracks `data-theme`
/// changes without the editor having to be rebuilt.
export function attachThemeSync(view: EditorView, compartment: Compartment): (theme: Theme) => void {
  return (theme: Theme) => {
    view.dispatch({ effects: compartment.reconfigure(editorThemeFor(theme)) });
  };
}

interface ToolbarOptions {
  host: HTMLElement;
  onRun: () => void | Promise<void>;
  onReset: () => void | Promise<void>;
  onNew: () => void | Promise<void>;
  onSave: () => void | Promise<void>;
  /// Save the active tab under a new name (always prompts). Wired in
  /// main.ts; the toolbar exposes it as a button + Ctrl+Shift+S.
  onSaveAs?: () => void | Promise<void>;
  onPickFolder: () => void | Promise<void>;
  onParseAll: () => void | Promise<void>;
  /// Copy a "Run this in k0-lang →" deep link for the current buffer.
  /// Wired in main.ts; the toolbar exposes it as a button + palette
  /// command.
  onShareLink?: () => void | Promise<void>;
  onThemeChange: (theme: Theme) => void;
  /// Enter tutorial mode + open the last/first lesson. Wired in
  /// main.ts; backs the persistent Learn affordance.
  onLearn?: () => void;
  /// Open the login modal. Wired in main.ts.
  onSignIn?: () => void;
  /// Sign the user out + refresh dependent state. Wired in main.ts.
  onSignOut?: () => void;
  /// Open the terms dialog from the account-menu reminder. Wired in main.ts.
  onAcceptTerms?: () => void;
}

/// Returned by `attachToolbar`. Lets `main.ts` call back in when
/// state outside the toolbar changes — e.g. the active tab switching
/// to a lesson should disable Save.
export interface ToolbarApi {
  setSaveEnabled(enabled: boolean, reason?: string): void;
  /// Re-render the right-edge account widget. Call after sign-in /
  /// sign-out so the email + dropdown reflect the new state.
  refreshAccount(user: { email: string; termsPending: boolean } | null): void;
  /// Reflect tutorial-open state on the Learn toggle (icon, label,
  /// aria-pressed). Driven from main.ts's `setTutorialMode` so the
  /// button stays in sync no matter which entry point flipped the mode.
  setLearnActive(active: boolean): void;
}

export function attachToolbar(opts: ToolbarOptions): ToolbarApi {
  opts.host.classList.add('toolbar');
  opts.host.innerHTML = '';

  const runBtn = makeIconButton('run', 'run', 'btn btn-primary');
  runBtn.title = 'Execute the current program against in-browser DuckDB-WASM (Ctrl+Enter)';
  runBtn.addEventListener('click', async () => {
    runBtn.disabled = true;
    // Swap only the label text — `setButtonLabel` leaves the SVG alone
    // (overwriting `.textContent` here would wipe it).
    setButtonLabel(runBtn, 'running');
    try { await opts.onRun(); }
    finally {
      runBtn.disabled = false;
      setButtonLabel(runBtn, 'run');
    }
  });
  opts.host.appendChild(runBtn);

  const resetBtn = makeIconButton('reset', 'reset', 'btn');
  resetBtn.title = 'Drop the in-browser WORK schema and clear the output panel';
  resetBtn.addEventListener('click', () => void opts.onReset());
  opts.host.appendChild(resetBtn);

  const newBtn = makeIconButton('new', 'new', 'btn');
  newBtn.title = 'Open a fresh untitled .sas tab';
  newBtn.addEventListener('click', () => void opts.onNew());
  opts.host.appendChild(newBtn);

  const saveBtn = makeIconButton('save', 'save', 'btn');
  saveBtn.title = 'Save the active tab (Ctrl+S)';
  saveBtn.addEventListener('click', () => void opts.onSave());
  opts.host.appendChild(saveBtn);

  const saveAsBtn = makeIconButton('save-as', 'save as', 'btn');
  saveAsBtn.title = 'Save the active tab under a new name (Ctrl+Shift+S)';
  saveAsBtn.addEventListener('click', () => void opts.onSaveAs?.());
  opts.host.appendChild(saveAsBtn);

  const pickBtn = makeIconButton('open', 'open', 'btn');
  pickBtn.title = 'Open a folder of .sas files';
  pickBtn.addEventListener('click', () => void opts.onPickFolder());
  opts.host.appendChild(pickBtn);

  if (opts.onShareLink) {
    const shareBtn = makeIconButton('link', '', 'btn btn-icon');
    shareBtn.title = 'Copy a shareable link that prefills + runs this program';
    shareBtn.setAttribute('aria-label', 'Copy shareable link');
    shareBtn.addEventListener('click', () => void opts.onShareLink?.());
    opts.host.appendChild(shareBtn);
  }

  // `parse all` is a niche batch tool — keep it discoverable through
  // the command palette, but drop it from the toolbar to free space
  // for higher-traffic actions.

  // One spacer pushes the right cluster (theme / palette / Learn /
  // account) to the far edge. The tutorial toggle moved to a toast +
  // palette command + the Learn affordance (§3), so the centred
  // two-spacer layout is gone.
  const spacer = document.createElement('div');
  spacer.className = 'toolbar-spacer';
  opts.host.appendChild(spacer);

  // Theme toggle carries an icon + label. The icon shows the theme
  // you'd switch TO (sun while dark, moon while light); both the icon
  // and the label swap on click via dedicated helpers so neither
  // clobbers the other.
  const themeBtn = makeIconButton('theme-light', 'light', 'btn');
  const renderThemeIcon = (t: Theme) => {
    setButtonIcon(themeBtn, t === 'dark' ? 'theme-light' : 'theme-dark');
    setButtonLabel(themeBtn, t === 'dark' ? 'light' : 'dark');
    themeBtn.title = t === 'dark'
      ? 'Switch to light theme'
      : 'Switch to dark theme';
  };
  let theme = readTheme();
  renderThemeIcon(theme);
  themeBtn.addEventListener('click', () => {
    theme = theme === 'dark' ? 'light' : 'dark';
    writeTheme(theme);
    renderThemeIcon(theme);
    opts.onThemeChange(theme);
  });
  opts.host.appendChild(themeBtn);

  const paletteBtn = makeIconButton('palette', '', 'btn btn-icon');
  paletteBtn.title = 'Command palette (Ctrl+;)';
  paletteBtn.setAttribute('aria-label', 'Command palette');
  paletteBtn.addEventListener('click', () => openPalette());
  opts.host.appendChild(paletteBtn);

  // Persistent "Learn" affordance — always visible, toggles the tutorial
  // (toast + palette are the other two entry points). Click is wired by
  // main.ts via onLearn so the open/close decision + sequencing live in
  // one place; the button only mirrors state via `setLearnActive`.
  const learnBtn = makeIconButton('learn', '', 'btn btn-icon');
  const renderLearn = (active: boolean): void => {
    setButtonIcon(learnBtn, active ? 'close' : 'learn');
    const label = active ? 'Close the tutorial' : 'Open the interactive SAS tutorial';
    learnBtn.title = label;
    learnBtn.setAttribute('aria-label', label);
    learnBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
  };
  renderLearn(false);
  learnBtn.addEventListener('click', () => opts.onLearn?.());
  opts.host.appendChild(learnBtn);

  // Account widget lives at the far right. Built as a placeholder
  // container; `refreshAccount` fills it in based on auth state.
  const accountHost = document.createElement('div');
  accountHost.className = 'account-host';
  opts.host.appendChild(accountHost);

  const renderAccount = (user: { email: string; termsPending: boolean } | null): void => {
    accountHost.innerHTML = '';
    if (!user) {
      const signInBtn = makeButton('sign in', 'btn');
      signInBtn.title = 'Sign in to use the editor';
      signInBtn.addEventListener('click', () => opts.onSignIn?.());
      accountHost.appendChild(signInBtn);
      return;
    }
    const details = document.createElement('details');
    details.className = 'account-widget';
    const summary = document.createElement('summary');
    // CSS truncates a long email; the title keeps the full address.
    summary.textContent = user.email;
    summary.title = user.email;
    if (user.termsPending) details.classList.add('account-attention');
    details.appendChild(summary);

    const menu = document.createElement('div');
    menu.className = 'account-menu';
    const emailRow = document.createElement('div');
    emailRow.className = 'account-menu-email';
    emailRow.textContent = user.email;
    menu.appendChild(emailRow);
    if (user.termsPending) {
      const termsBtn = document.createElement('button');
      termsBtn.type = 'button';
      termsBtn.className = 'account-menu-action account-menu-attention';
      termsBtn.textContent = 'accept the terms of service';
      termsBtn.title = 'The terms changed. Accept them to use your account\'s features.';
      termsBtn.addEventListener('click', () => {
        details.removeAttribute('open');
        opts.onAcceptTerms?.();
      });
      menu.appendChild(termsBtn);
    }
    const legal = document.createElement('div');
    legal.className = 'account-menu-links';
    for (const [href, text] of [
      ['https://kolistat.com/terms', 'terms'],
      ['https://kolistat.com/privacy', 'privacy'],
      ['/third-party-licenses.txt', 'licences'],
    ]) {
      const a = document.createElement('a');
      a.href = href;
      a.textContent = text;
      a.target = '_blank';
      a.rel = 'noopener';
      legal.appendChild(a);
    }
    menu.appendChild(legal);
    const signOutBtn = document.createElement('button');
    signOutBtn.type = 'button';
    signOutBtn.className = 'account-menu-action';
    signOutBtn.textContent = 'sign out';
    signOutBtn.addEventListener('click', () => {
      details.removeAttribute('open');
      opts.onSignOut?.();
    });
    menu.appendChild(signOutBtn);
    details.appendChild(menu);

    // Click anywhere else closes the menu — `<details>`'s native
    // behaviour only closes when summary is re-clicked.
    const onDocClick = (ev: MouseEvent): void => {
      if (!details.contains(ev.target as Node)) details.removeAttribute('open');
    };
    document.addEventListener('click', onDocClick);
    // Replaced on next refreshAccount; cleanup handled by innerHTML clear.

    accountHost.appendChild(details);
  };
  renderAccount(null);

  // Keyboard shortcuts at the document level. Ctrl+; opens the
  // palette; Reset stays mouse-only (destructive). Ctrl+Enter is
  // handled inside the CodeMirror keymap (editorActionsKeymap in
  // main.ts) so it can run the selection when one exists and avoid
  // racing the editor's default newline-insert behaviour.
  document.addEventListener('keydown', (ev) => {
    if (ev.ctrlKey && (ev.key === ';' || ev.code === 'Semicolon')) {
      ev.preventDefault();
      openPalette();
    }
  });

  // Register the toolbar's actions in the global command registry so
  // the palette can reach them by name.
  commands.register({ id: 'run', label: 'Run program', shortcut: 'Ctrl+Enter', run: opts.onRun });
  commands.register({ id: 'reset', label: 'Reset session (drop WORK schema)', run: opts.onReset });
  commands.register({ id: 'new', label: 'New untitled tab', run: opts.onNew });
  commands.register({ id: 'save', label: 'Save active tab', shortcut: 'Ctrl+S', run: opts.onSave });
  if (opts.onSaveAs) {
    commands.register({ id: 'save-as', label: 'Save active tab as…', shortcut: 'Ctrl+Shift+S', run: opts.onSaveAs });
  }
  commands.register({ id: 'pick-folder', label: 'Open folder…', run: opts.onPickFolder });
  commands.register({ id: 'parse-all', label: 'Parse all .sas files in folder', run: opts.onParseAll });
  if (opts.onShareLink) {
    commands.register({ id: 'share-link', label: 'Copy shareable link', run: opts.onShareLink });
  }
  commands.register({
    id: 'theme-toggle',
    label: 'Toggle light / dark theme',
    run: () => themeBtn.click(),
  });

  return {
    setSaveEnabled(enabled, reason) {
      const title = enabled
        ? 'Save the active tab (Ctrl+S)'
        : (reason ?? 'Save unavailable for this tab.');
      saveBtn.disabled = !enabled;
      saveBtn.title = title;
      // Save As shares the lesson read-only gate — keep them in lockstep.
      saveAsBtn.disabled = !enabled;
      saveAsBtn.title = enabled
        ? 'Save the active tab under a new name (Ctrl+Shift+S)'
        : (reason ?? 'Save unavailable for this tab.');
    },
    refreshAccount(user) {
      renderAccount(user);
    },
    setLearnActive(active) {
      renderLearn(active);
    },
  };
}

function makeButton(text: string, cls: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = cls;
  btn.textContent = text;
  return btn;
}

/// Button carrying a leading SVG icon and (optionally) a text label.
/// The label lives in a dedicated `<span class="btn-label">` so callers
/// can swap the text (busy-state, theme toggle) without ever touching
/// the SVG via `.textContent`. An empty label yields an icon-only
/// button (palette / Learn).
function makeIconButton(name: IconName, label: string, cls: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = cls;
  btn.appendChild(icon(name));
  if (label) {
    const span = document.createElement('span');
    span.className = 'btn-label';
    span.textContent = label;
    btn.appendChild(span);
  }
  return btn;
}

/// Replace the SVG icon of an icon-button in place, preserving the
/// label span. Used by the theme toggle to swap moon ⇄ sun.
function setButtonIcon(btn: HTMLButtonElement, name: IconName): void {
  const existing = btn.querySelector('svg');
  const next = icon(name);
  if (existing) existing.replaceWith(next);
  else btn.prepend(next);
}

/// Set the text of an icon-button's label span without disturbing the
/// SVG. Used by the run busy-state.
function setButtonLabel(btn: HTMLButtonElement, label: string): void {
  const span = btn.querySelector<HTMLSpanElement>('.btn-label');
  if (span) span.textContent = label;
}

// --- Command palette ------------------------------------------------------

function openPalette(): void {
  const dialog = document.getElementById('cmd-palette') as HTMLDialogElement | null;
  if (!dialog) return;
  buildPalette(dialog);
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
  const input = dialog.querySelector<HTMLInputElement>('.palette-input');
  input?.focus();
  input?.select();
}

function buildPalette(dialog: HTMLDialogElement): void {
  if (dialog.dataset.built === 'true') {
    // Refresh list to reflect any commands registered after the dialog
    // was built. The input's value is preserved.
    const list = dialog.querySelector<HTMLUListElement>('.palette-list');
    const input = dialog.querySelector<HTMLInputElement>('.palette-input');
    if (list && input) renderPaletteList(list, input.value, dialog);
    return;
  }
  dialog.classList.add('palette');
  dialog.innerHTML = '';
  const input = document.createElement('input');
  input.className = 'palette-input';
  input.type = 'text';
  input.placeholder = 'Type to filter commands…';
  input.autocomplete = 'off';
  input.spellcheck = false;
  dialog.appendChild(input);

  const list = document.createElement('ul');
  list.className = 'palette-list';
  dialog.appendChild(list);

  const closeOnClickOutside = (ev: MouseEvent) => {
    const r = dialog.getBoundingClientRect();
    if (
      ev.clientX < r.left || ev.clientX > r.right ||
      ev.clientY < r.top || ev.clientY > r.bottom
    ) {
      dialog.close();
    }
  };
  dialog.addEventListener('click', closeOnClickOutside);

  input.addEventListener('input', () => renderPaletteList(list, input.value, dialog));
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') { dialog.close(); return; }
    const items = Array.from(list.querySelectorAll<HTMLLIElement>('li'));
    const active = list.querySelector<HTMLLIElement>('li[data-active="true"]');
    let idx = active ? items.indexOf(active) : -1;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      idx = Math.min(items.length - 1, idx + 1);
      activateItem(items, idx);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      idx = Math.max(0, idx - 1);
      activateItem(items, idx);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      const target = active ?? items[0];
      const id = target?.dataset.id;
      if (id) {
        dialog.close();
        void commands.run(id);
      }
    }
  });

  renderPaletteList(list, '', dialog);
  dialog.dataset.built = 'true';
}

function renderPaletteList(list: HTMLUListElement, query: string, dialog: HTMLDialogElement): void {
  list.innerHTML = '';
  const matches = commands.filter(query);
  matches.forEach((cmd, i) => {
    const li = document.createElement('li');
    li.dataset.id = cmd.id;
    if (i === 0) li.dataset.active = 'true';
    const label = document.createElement('span');
    label.className = 'palette-label';
    label.textContent = cmd.label;
    li.appendChild(label);
    if (cmd.shortcut) {
      const kbd = document.createElement('kbd');
      kbd.textContent = cmd.shortcut;
      li.appendChild(kbd);
    }
    li.addEventListener('click', () => {
      dialog.close();
      void commands.run(cmd.id);
    });
    list.appendChild(li);
  });
  if (matches.length === 0) {
    const empty = document.createElement('li');
    empty.className = 'palette-empty';
    empty.textContent = 'No matching commands.';
    list.appendChild(empty);
  }
}

function activateItem(items: HTMLLIElement[], idx: number): void {
  for (const it of items) delete it.dataset.active;
  const target = items[idx];
  if (target) {
    target.dataset.active = 'true';
    target.scrollIntoView({ block: 'nearest' });
  }
}
